import React, { memo, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';
import { ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faWarehouse,
  faCircleNotch,
} from '@fortawesome/free-solid-svg-icons';
import { Img } from 'react-image';
import Select from 'react-select';

import TemplateService from '../../Services/TemplateService';
import ModalLayout from '../ModalLayout';
import { BASIC_SELECT_STYLES } from '../../Styles/SelectStyles';
import {
  DF_PROVISIONING_MODEL_ID,
  PROVISIONING_MODEL_ID,
  XIRGO_PROVISIONING_MODEL_ID,
  XIRGO_V2_PROVISIONING_MODEL_ID,
} from '../../Constants/StaticUUIDs';
import {
  XIRGO_V1_VENDOR_ID,
  XIRGO_V2_VENDOR_ID,
} from '../../Constants/Vendors';

export default memo(MoveAssetModalDisplay);

function MoveAssetModalDisplay(props) {
  const {
    error,
    handleOrgNameSearch,
    handleMoveButtonClick,
    handleBackClick,
    handleModelSelect,
    handleModelTypeSelect,
    handleMoveOrg,
    handleOrgSelect,
    isOpen,
    moveRequestPending,
    orgNameSearch,
    orgsMap,
    orgTemplateOptions,
    orgTemplatesLoading,
    selectedAsset,
    selectedOrgID,
    sharedGtemplate,
    toggleModal,
    viewing,
  } = props;
  const restrictedVendors = ['1', XIRGO_V1_VENDOR_ID];

  const orgRows = useMemo(() => {
    return Object.entries(orgsMap)
      .filter(([id, { features, name = '' }]) => {
        if (id === selectedAsset.orgID) return false;
        if (
          restrictedVendors.includes(selectedAsset.vendor) &&
          !features?.vendors?.includes?.(selectedAsset.vendor)
        )
          return false;
        if (!orgNameSearch) return true;
        return name.toLowerCase().includes(orgNameSearch.toLowerCase());
      })
      .map(([id, { name, logo }]) => ({ id, name, logo }))
      .sort(({ name: a }, { name: b }) => a.localeCompare(b));
  }, [orgNameSearch]);

  const hasProvisioningModel = [
    DF_PROVISIONING_MODEL_ID,
    PROVISIONING_MODEL_ID,
    XIRGO_PROVISIONING_MODEL_ID,
    XIRGO_V2_PROVISIONING_MODEL_ID,
  ].includes(selectedAsset.gtemplateID);

  const submitIsDisabled = useMemo(() => {
    switch (true) {
      case props.modelType === 'keepShared':
      case hasProvisioningModel && props.modelType === 'keepProvision':
        return false;
      case hasProvisioningModel &&
        props.modelType !== 'keepProvision' &&
        !props.selectedModel:
        return true;
      case !hasProvisioningModel && !props.selectedModel:
        return true;
      default:
        return false;
    }
  }, [hasProvisioningModel, props.modelType, props.selectedModel]);

  return (
    <ModalLayout
      isOpen={isOpen}
      scrollable={viewing !== 'select'}
      toggle={() => toggleModal()}
    >
      <ModalHeader className="border-0" toggle={() => toggleModal()}>
        {viewing === 'select' ? 'Sub Organizations' : 'Others may lose access'}
      </ModalHeader>
      <ModalBody className="d-flex flex-column">
        <div className={viewing === 'select' ? 'input-group' : 'd-none'}>
          <input
            type="text"
            name={name}
            value={orgNameSearch}
            onChange={handleOrgNameSearch}
            className="form-control border-right-0"
            placeholder="Filter by name"
            aria-label="Search by organization name"
            aria-describedby="spyglass-icon"
          />
          <div className="input-group-append">
            <span
              className="input-group-text bg-white border-left-0"
              id="spyglass-icon"
            >
              <FontAwesomeIcon
                className="text-text-light-gray"
                icon={faSearch}
              />
            </span>
          </div>
        </div>
        {viewing === 'select' ? (
          <OrgsList
            handleOrgSelect={handleOrgSelect}
            orgRows={orgRows}
            selectedOrgID={selectedOrgID}
          />
        ) : (
          <ConfirmAndModelSelect
            handleModelSelect={handleModelSelect}
            handleModelTypeSelect={handleModelTypeSelect}
            modelType={props.modelType}
            orgsMap={orgsMap}
            orgTemplateOptions={orgTemplateOptions}
            orgTemplatesLoading={orgTemplatesLoading}
            selectedAsset={selectedAsset}
            selectedModel={props.selectedModel}
            selectedOrgID={selectedOrgID}
            sharedGtemplate={sharedGtemplate}
          />
        )}
        <p className={`mb-0 ${error ? 'text-danger text-right' : 'd-none'}`}>
          {error}
        </p>
      </ModalBody>
      <ModalFooter
        className={`border-0 ${
          viewing === 'select' ? '' : 'justify-content-between'
        }`}
      >
        {viewing === 'select' ? (
          <button
            className="btn modal-button-secondary p-0 mr-3 nine-tenth-text"
            style={{ width: '7rem' }}
            onClick={handleMoveButtonClick}
            disabled={orgTemplatesLoading || !selectedOrgID.length}
          >
            Move Here
          </button>
        ) : (
          <>
            <button
              className="btn btn-outline-secondary p-0 px-3 nine-tenth-text"
              onClick={handleBackClick}
            >
              Back
            </button>
            <div>
              <button
                className="btn modal-button-submit p-0 mr-2 px-3 nine-tenth-text"
                disabled={submitIsDisabled}
                onClick={handleMoveOrg}
              >
                {moveRequestPending ? (
                  <FontAwesomeIcon icon={faCircleNotch} size="xs" />
                ) : (
                  'Ok'
                )}
              </button>
              <button
                className="btn btn-outline-secondary p-0 px-3 nine-tenth-text shadow-none"
                onClick={() => toggleModal()}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </ModalFooter>
      <style jsx>{`
        .btn {
          outline: none !important;
        }
      `}</style>
    </ModalLayout>
  );
}

MoveAssetModalDisplay.propTypes = {
  error: PropTypes.string,
  handleBackClick: PropTypes.func,
  handleModelSelect: PropTypes.func,
  handleModelTypeSelect: PropTypes.func,
  handleMoveButtonClick: PropTypes.func,
  handleMoveOrg: PropTypes.func,
  handleOrgNameSearch: PropTypes.func,
  handleOrgSelect: PropTypes.func,
  isOpen: PropTypes.bool,
  modelType: PropTypes.oneOf([
    'keepProvision',
    'keepShared',
    'cloneModel',
    'selectModel',
  ]),
  moveRequestPending: PropTypes.bool,
  orgsMap: PropTypes.object,
  orgNameSearch: PropTypes.string,
  orgTemplatesLoading: PropTypes.bool,
  orgTemplateOptions: PropTypes.arrayOf(PropTypes.object),
  selectedAsset: PropTypes.object,
  selectedModel: PropTypes.object,
  selectedOrgID: PropTypes.string,
  sharedGtemplate: PropTypes.shape({ orgID: PropTypes.string }),
  toggleModal: PropTypes.func,
  viewing: PropTypes.string,
};

MoveAssetModalDisplay.defaultProps = {
  selectedAsset: { orgID: '' },
  orgsMap: { '': { name: '' } },
  selectedOrgID: '',
};

function OrgsList({ handleOrgSelect, orgRows, selectedOrgID }) {
  const fallback = <FontAwesomeIcon icon={faWarehouse} />;

  return (
    <div className="flex-fill position-relative">
      <div className="position-absolute h-100 w-100 container-fluid overflow-auto">
        {orgRows.map(({ id, name, logo }) => (
          <div
            className={`row org-container-row ${
              selectedOrgID === id && 'modal-button-primary'
            }`}
            role="option"
            aria-selected={selectedOrgID === id}
            tabIndex="0"
            key={id}
            onClick={() => handleOrgSelect(id)}
            onKeyUp={() => handleOrgSelect(id)}
          >
            <div className="col-3 d-flex align-items-center justify-content-center">
              <Img
                src={logo}
                loader={fallback}
                unloader={fallback}
                className="img-fluid"
                alt="logo"
                style={{ maxHeight: '2rem' }}
              />
            </div>
            <div className="col-9 h-100 pl-0">
              <span className="org-name-text d-block text-truncate">
                {name}
              </span>
            </div>
          </div>
        ))}
        <style jsx>{`
          .org-container-row {
            height: 2.5rem;
            outline: none;
            transition: all 0.15s ease 0s;
          }
          .org-conatiner-row:focus {
            outline: none;
          }
          .org-name-text {
            line-height: 40px;
          }
        `}</style>
      </div>
    </div>
  );
}

OrgsList.propTypes = {
  handleOrgSelect: PropTypes.func,
  orgRows: PropTypes.arrayOf(PropTypes.object),
  selectedOrgID: PropTypes.string,
};

function ConfirmAndModelSelect({
  handleModelSelect,
  handleModelTypeSelect,
  modelType,
  orgsMap,
  orgTemplateOptions,
  orgTemplatesLoading,
  selectedAsset,
  selectedModel,
  selectedOrgID,
  sharedGtemplate,
}) {
  const { path = '' } = orgsMap[selectedOrgID] ?? {};
  const validSharedGtemplate = path.includes(sharedGtemplate?.orgID);
  const [targetOrgModels, updateTargetOrgModels] = useState({
    loading: !validSharedGtemplate,
    options: [],
  });
  const source = CancelToken.source();
  const templateService = useMemo(() => new TemplateService(), []);

  const hasProvisioningModel = [
    DF_PROVISIONING_MODEL_ID,
    PROVISIONING_MODEL_ID,
    XIRGO_PROVISIONING_MODEL_ID,
    XIRGO_V2_PROVISIONING_MODEL_ID,
  ].includes(selectedAsset.gtemplateID);
  const selectLoading =
    modelType === 'cloneModel' ? orgTemplatesLoading : targetOrgModels.loading;
  const selectOptions =
    modelType === 'cloneModel' ? orgTemplateOptions : targetOrgModels.options;

  const fetchTargetOrgModels = async () => {
    const limit = 100;
    const order = 'asc';
    const page = 1;
    const sort = 'name';
    const vendor =
      selectedAsset.vendor === XIRGO_V1_VENDOR_ID
        ? XIRGO_V1_VENDOR_ID
        : selectedAsset.vendor === XIRGO_V2_VENDOR_ID
          ? XIRGO_V2_VENDOR_ID
          : selectedAsset.vendor === 'danfoss'
            ? '1'
            : 'elevat';
    try {
      const { count, data } = await templateService.fetchPaginatedTemplates(
        page,
        selectedOrgID,
        limit,
        sort,
        order,
        undefined,
        vendor,
        source.token
      );
      const availableTemplates = transformTemplateData(data);
      if (count <= data.length) {
        updateTargetOrgModels({ loading: false, options: availableTemplates });
      } else {
        const additionalData =
          await templateService.handleAdditionalTemplateRequests({
            cancelToken: source.token,
            count,
            limit,
            order,
            orgID: selectedOrgID,
            sort,
            vendor,
          });

        const addtionalTemplateOptions = transformTemplateData(additionalData);
        updateTargetOrgModels({
          loading: false,
          options: availableTemplates.concat(...addtionalTemplateOptions),
        });
      }
    } catch (err) {
      if (isCancel(err)) return;
      updateTargetOrgModels({ ...targetOrgModels, loading: false });
    }
  };

  useEffect(() => {
    if (['cloneModel', 'keepShared'].includes(modelType)) return;
    fetchTargetOrgModels();
    return source.cancel;
  }, [modelType, selectedOrgID]);

  return (
    <div className="container-fluid text-text-light-gray">
      <div className="row mt-4">
        <p className="confirmation-text col-12 mb-0">
          Moving
          <b className="text-gray">
            {` ${selectedAsset.nickname} ` || ' this asset '}
          </b>
          to
          <b className="text-gray">
            {` ${orgsMap[selectedOrgID].name} ` || ' '}
          </b>
          will make it visible to everyone in this organization. They will see
          this asset has been moved into their asset inventory, and be able to
          view its info. In order to receive data from this asset, a new
          template will have to be set.
        </p>
      </div>
      <div className="row mt-4">
        <p className="confirmation-text col-12 mb-0">
          Users in{` ${orgsMap[selectedAsset.orgID].name} ` || ' '}will no
          longer be able to see the asset. It will be moved from
          {` ${orgsMap[selectedAsset.orgID].name}'s ` || ' '}inventory to
          {` ${orgsMap[selectedOrgID].name}'s ` || ' '}asset inventory.
        </p>
      </div>
      {hasProvisioningModel ? (
        <div className="row mt-4">
          <p className="mb-0 col-12 col-md-8 col-lg-6 text-text-light-gray three-quarter-text">
            This asset is currently assigned the Provisioning Model. It can be
            moved as is or you can choose to select a new Model below.
          </p>
        </div>
      ) : null}
      <>
        <div className={`row ${hasProvisioningModel ? 'mt-2' : 'mt-4'}`}>
          <p className="col-12 mb-0 font-weight-bolder">Asset Model</p>
        </div>
        <div className="row mt-2">
          {[
            ['keepProvision', 'Keep Provisioning Model'],
            ['cloneModel', 'Clone a Model from this Organization'],
            ['selectModel', 'Select a Model from the target Organization'],
            ['keepShared', 'Keep the assigned Shared Model'],
          ].map(([type, displayText]) => {
            switch (true) {
              case type === 'cloneModel' &&
                selectedAsset.vendor === XIRGO_V2_VENDOR_ID:
                return null;
              case !['keepProvision', 'keepShared'].includes(type):
              case type === 'keepProvision' && hasProvisioningModel:
              case type === 'keepShared' && validSharedGtemplate:
                return (
                  <div className="col-12" key={type}>
                    <div className="form-check">
                      <input
                        checked={modelType === type}
                        className="form-check-input"
                        name="modelType"
                        onChange={handleModelTypeSelect}
                        type="radio"
                        value={type}
                      />
                      <label className="form-check-label">{displayText}</label>
                    </div>
                  </div>
                );
              default:
                return null;
            }
          })}
        </div>
        {modelType === 'keepShared' ? null : (
          <div className="row mt-4">
            <div className="col-12 col-md-8 col-lg-6">
              <Select
                isLoading={selectLoading}
                isSearchable
                maxMenuHeight={150}
                onChange={handleModelSelect}
                options={selectOptions}
                placeholder="Select an Asset Model"
                styles={BASIC_SELECT_STYLES}
                value={selectedModel}
              />
            </div>
          </div>
        )}
      </>
      <style jsx>{`
        .confirmation-text {
          font-size: 0.75rem;
        }
        @media (min-width: 768px) {
          .confirmation-text {
            font-size: 1rem;
          }
        }
      `}</style>
    </div>
  );
}

ConfirmAndModelSelect.propTypes = {
  handleModelSelect: PropTypes.func,
  handleModelTypeSelect: PropTypes.func,
  modelType: PropTypes.oneOf([
    'keepProvision',
    'keepShared',
    'cloneModel',
    'selectModel',
  ]),
  orgsMap: PropTypes.object,
  orgTemplateOptions: PropTypes.arrayOf(PropTypes.object),
  orgTemplatesLoading: PropTypes.bool,
  selectedAsset: PropTypes.shape({
    _id: PropTypes.string,
    gtemplateID: PropTypes.string,
    nickname: PropTypes.string,
    orgID: PropTypes.string,
    vendor: PropTypes.oneOf([
      '1',
      'elevat',
      XIRGO_V1_VENDOR_ID,
      XIRGO_V2_VENDOR_ID,
    ]),
  }),
  selectedModel: PropTypes.object,
  selectedOrgID: PropTypes.string,
  sharedGtemplate: PropTypes.shape({ orgID: PropTypes.string }),
};
ConfirmAndModelSelect.defaultProps = { selectedAsset: {} };

export function transformTemplateData(templates) {
  if (!Array.isArray(templates)) return [];
  const availableTemplates = templates
    .filter(({ _id }) => {
      return ![
        DF_PROVISIONING_MODEL_ID,
        PROVISIONING_MODEL_ID,
        XIRGO_PROVISIONING_MODEL_ID,
        XIRGO_V2_PROVISIONING_MODEL_ID,
      ].includes(_id);
    })
    .map(({ _id: value, name: label } = {}) => {
      return { label, value };
    });
  return availableTemplates;
}
